import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { gbpSigned, fullDate, toGbp, curSymbol } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Repeat, Zap, Receipt, Download } from "lucide-react";

const FILTERS = ["All", "Groceries", "Dining", "Entertainment", "Shopping", "Transport", "Bills", "Subscriptions", "Income"];

const MONTH_OPTIONS = (() => {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    opts.push({
      value: `${d.getFullYear()}-${d.getMonth() + 1}`,
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      label: d.toLocaleDateString("en-GB", { month: "short", year: "numeric" }),
    });
  }
  return opts;
})();

export default function TransactionsLedger({ accounts = [], categories = [], reloadKey, onReload }) {
  const [txns, setTxns] = useState([]);
  const [filter, setFilter] = useState("All");
  const [exportMonth, setExportMonth] = useState(MONTH_OPTIONS[0].value);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [simOpen, setSimOpen] = useState(false);
  const [sim, setSim] = useState({ account_id: "", merchant: "", amount: "", category: "Dining" });
  const [simSaving, setSimSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "All") params.category = filter;
      if (search) params.search = search;
      const { data } = await api.get("/transactions", { params });
      setTxns(data.transactions);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load, reloadKey]);

  const runSimulate = async () => {
    if (!sim.account_id || !sim.merchant || !sim.amount) {
      toast.error("Fill in account, merchant and amount");
      return;
    }
    setSimSaving(true);
    try {
      const { data } = await api.post("/transactions/simulate", {
        account_id: sim.account_id,
        merchant: sim.merchant,
        amount: toGbp(sim.amount),
        category: sim.category,
      });
      if (data.alert) {
        toast.warning(`Alert triggered: ${data.alert.message}`, { description: "We've emailed you a spend alert too." });
      } else {
        toast.success("Transaction posted");
      }
      setSimOpen(false);
      setSim({ account_id: "", merchant: "", amount: "", category: "Dining" });
      load();
      onReload?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSimSaving(false);
    }
  };

  const exportCsv = async () => {
    const opt = MONTH_OPTIONS.find((o) => o.value === exportMonth) || MONTH_OPTIONS[0];
    setExporting(true);
    try {
      const { data } = await api.get("/transactions/export", {
        params: { year: opt.year, month: opt.month },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `finlo-statement-${opt.year}-${String(opt.month).padStart(2, "0")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${opt.label} statement`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="mm-glow-card p-6" data-testid="transactions-ledger">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#00C896]">Ledger</p>
          <h3 className="font-display text-lg font-semibold mt-1 flex items-center gap-2"><Receipt className="h-5 w-5 text-slate-400" /> Recent transactions</h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <Select value={exportMonth} onValueChange={setExportMonth}>
              <SelectTrigger data-testid="export-month-select" className="h-9 w-[128px] bg-[#0D0D0D] border-white/10 text-white text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#14181D] border-white/10 text-white">
                {MONTH_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} className="focus:bg-[#00C896]/20">{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button data-testid="export-csv-button" onClick={exportCsv} disabled={exporting} size="sm" variant="outline" className="rounded-full border-white/15 bg-transparent text-slate-200 hover:bg-white/5 h-9">
              <Download className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Export CSV</span>
            </Button>
          </div>
          <div className="relative">
            <Search className="h-4 w-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="txn-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search merchant…"
              className="pl-9 h-9 w-full sm:w-56 bg-[#0D0D0D] border-white/10 text-white focus-visible:ring-[#00C896]"
            />
          </div>
          <Dialog open={simOpen} onOpenChange={setSimOpen}>
            <DialogTrigger asChild>
              <Button data-testid="simulate-txn-button" size="sm" variant="outline" disabled={accounts.length === 0} className="rounded-full border-[#00C896]/40 bg-transparent text-[#4DE3BF] hover:bg-[#00C896]/10 h-9">
                <Zap className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Test alert</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#14181D] border-white/10 text-white" data-testid="simulate-modal">
              <DialogHeader><DialogTitle className="font-display">Post a test transaction</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-400 -mt-1">Simulate a charge to see spend alerts fire in real time.</p>
              <div className="space-y-3 mt-2">
                <div>
                  <Label className="text-slate-300 text-sm">Account</Label>
                  <Select value={sim.account_id} onValueChange={(v) => setSim({ ...sim, account_id: v })}>
                    <SelectTrigger data-testid="sim-account-select" className="mt-1.5 bg-[#0D0D0D] border-white/10 text-white"><SelectValue placeholder="Choose account" /></SelectTrigger>
                    <SelectContent className="bg-[#14181D] border-white/10 text-white">
                      {accounts.map((a) => <SelectItem key={a.id} value={a.id} className="focus:bg-[#00C896]/20">{a.account_name} · {a.bank_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-300 text-sm">Merchant</Label>
                    <Input data-testid="sim-merchant-input" value={sim.merchant} onChange={(e) => setSim({ ...sim, merchant: e.target.value })} placeholder="Nando's" className="mt-1.5 bg-[#0D0D0D] border-white/10 text-white focus-visible:ring-[#00C896]" />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">Amount ({curSymbol()})</Label>
                    <Input data-testid="sim-amount-input" type="number" min="0" step="0.01" value={sim.amount} onChange={(e) => setSim({ ...sim, amount: e.target.value })} placeholder="120" className="mt-1.5 bg-[#0D0D0D] border-white/10 text-white focus-visible:ring-[#00C896]" />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Category</Label>
                  <Select value={sim.category} onValueChange={(v) => setSim({ ...sim, category: v })}>
                    <SelectTrigger data-testid="sim-category-select" className="mt-1.5 bg-[#0D0D0D] border-white/10 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#14181D] border-white/10 text-white">
                      {categories.filter((c) => c !== "Income").map((c) => <SelectItem key={c} value={c} className="focus:bg-[#00C896]/20">{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button data-testid="run-simulate-button" onClick={runSimulate} disabled={simSaving} className="w-full rounded-xl bg-[#00C896] hover:bg-[#00C896] text-[#04140D] font-semibold">Post transaction</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f}
            data-testid={`filter-${f}`}
            onClick={() => setFilter(f)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition ${filter === f ? "bg-[#00C896] text-[#04140D]" : "bg-[#0D0D0D] border border-white/10 text-slate-400 hover:text-white"}`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="mt-4 divide-y divide-white/6">
        {txns.map((t) => (
          <div key={t.id} data-testid={`txn-${t.id}`} className="flex items-center justify-between gap-3 py-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className="h-9 w-9 rounded-lg flex items-center justify-center text-white font-display font-bold text-xs shrink-0" style={{ background: t.bank_color || "#1f2b36" }}>
                {t.merchant?.[0]?.toUpperCase()}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate flex items-center gap-1.5">
                  {t.merchant}
                  {t.is_recurring && <Repeat className="h-3 w-3 text-[#00C896]" />}
                </p>
                <p className="text-[11px] text-slate-500">{fullDate(t.timestamp)} · {t.category} · {t.bank_name}</p>
              </div>
            </div>
            <span className={`font-num text-sm font-semibold shrink-0 ${t.amount >= 0 ? "text-[#00C896]" : "text-slate-200"}`}>
              {gbpSigned(t.amount)}
            </span>
          </div>
        ))}
        {!loading && txns.length === 0 && <p className="text-sm text-slate-500 py-6 text-center">No transactions found.</p>}
      </div>
    </div>
  );
}
